import { Link } from "react-router-dom";
import { Check, Phone } from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal, SectionHead } from "@/components/Reveal";
import { PACKAGES, CLINIC } from "@/lib/site";

const FAQS = [
  {
    q: "Do I need a doctor's referral to start physiotherapy?",
    a: "No referral is needed. You can book a comprehensive assessment directly, and our team will guide you on the right treatment plan from the very first session.",
  },
  {
    q: "Can I claim insurance reimbursement for sessions?",
    a: "Many insurers reimburse physiotherapy when prescribed alongside treatment. We provide complete invoices and documentation to support your claim — speak to our front desk for details.",
  },
  {
    q: "Do you accept walk-in patients?",
    a: "Yes. Walk-ins are welcome during clinic hours, though booked appointments get priority slots to avoid waiting.",
  },
  {
    q: "How many sessions will I need?",
    a: "That depends entirely on your condition — acute pain may respond in a few sessions, while post-surgical and neurological recovery follow structured multi-week protocols. Your assessment includes a clear session roadmap.",
  },
];

export default function Pricing() {
  return (
    <div data-testid="pricing-page" className="pt-[72px]">
      <Seo
        title="Packages & Pricing | Kamana Physiotherapy & Rehab Center, Lucknow"
        description="Transparent physiotherapy packages in Gomti Nagar, Lucknow — assessment, single-session pain relief, 7-session intensive rehab and complete recovery protocols."
      />

      <section className="bg-teal-obsidian text-alabaster grain-dark relative overflow-hidden">
        <div className="absolute inset-0 hero-glow" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 py-20 lg:py-24">
          <p className="mono-label text-gold">Packages &amp; Pricing</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight mt-4 leading-[1.06] max-w-3xl">
            Clear plans. <span className="italic text-gold">Honest pricing.</span> No surprises.
          </h1>
          <p className="mt-6 text-alabaster/70 max-w-2xl leading-relaxed">
            Every package begins with a proper diagnosis — because the right plan is always cheaper
            than ten wrong sessions.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-20 lg:py-24">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {PACKAGES.map((p, i) => (
            <Reveal key={p.name} delay={i * 0.08}>
              <div
                className={`h-full rounded-3xl p-8 flex flex-col card-lift ${
                  p.popular
                    ? "bg-teal-brand text-alabaster shadow-xl shadow-teal-brand/25 relative"
                    : "bg-white border border-teal-brand/10"
                }`}
              >
                {p.popular && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 mono-label bg-gold text-teal-obsidian !text-[9px] rounded-full px-4 py-1.5">
                    Most Popular
                  </span>
                )}
                <span className={`mono-label !text-[9px] ${p.popular ? "text-gold" : "text-gold-deep"}`}>{p.tag}</span>
                <h3 className={`font-display text-2xl font-semibold mt-3 ${p.popular ? "text-alabaster" : "text-teal-brand"}`}>
                  {p.name}
                </h3>
                <div className="mt-4 flex items-baseline gap-2">
                  <p className={`font-display text-4xl font-semibold ${p.popular ? "text-gold" : "text-teal-brand"}`}>{p.price}</p>
                </div>
                <p className={`text-xs mt-1 ${p.popular ? "text-alabaster/60" : "text-inkmuted"}`}>{p.duration}</p>
                <ul className="mt-6 space-y-3 flex-1">
                  {p.features.map((f) => (
                    <li key={f} className={`flex items-start gap-2.5 text-xs leading-relaxed ${p.popular ? "text-alabaster/80" : "text-inkmuted"}`}>
                      <Check size={14} className={`shrink-0 mt-0.5 ${p.popular ? "text-gold" : "text-teal-mint"}`} />
                      {f}
                    </li>
                  ))}
                </ul>
                <Link
                  to="/book-appointment"
                  data-testid={`pricing-book-${p.name.toLowerCase().replace(/[^a-z]+/g, "-").slice(0, 25)}`}
                  className={`mt-7 text-center text-sm font-semibold rounded-full py-3.5 transition-colors duration-200 ${
                    p.popular
                      ? "bg-gold text-teal-obsidian hover:bg-alabaster"
                      : "border border-teal-brand/25 text-teal-brand hover:bg-teal-soft"
                  }`}
                >
                  Book This Package
                </Link>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-teal-soft/60 border-y border-teal-brand/10">
        <div className="max-w-4xl mx-auto px-5 lg:px-8 py-20">
          <SectionHead label="Common Questions" title="Before You Book" />
          <div className="mt-10 space-y-4">
            {FAQS.map((f, i) => (
              <Reveal key={f.q} delay={i * 0.07}>
                <div className="bg-white border border-teal-brand/10 rounded-2xl p-6">
                  <h3 className="font-semibold text-teal-brand text-sm sm:text-base">{f.q}</h3>
                  <p className="mt-2.5 text-sm text-inkmuted leading-relaxed">{f.a}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.25}>
            <div className="mt-10 text-center">
              <a
                href={CLINIC.phoneHref}
                data-testid="pricing-call-cta"
                className="inline-flex items-center gap-2 bg-teal-brand text-alabaster font-semibold rounded-full px-8 py-4 text-sm hover:bg-teal-deep transition-colors"
              >
                <Phone size={16} /> Talk to Us — {CLINIC.phone}
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
